import { disableBeforeOpenDay, eventOpenDay } from '../../utils/openDayDate';
/** @jsxImportSource @emotion/react */
/**
 * ActionWizard — Unified Event/Task Creation Modal
 *
 * Replaces both CreateEventModal and CreateTaskFromTemplateModal
 * with a single 2-step wizard:
 *   Step 1: Select action template (ActionGrid) + mode toggle (immediate/assign)
 *   Step 2: Fill properties (immediate) or set task params (assign)
 *
 * Works for:
 * - Single planting page (one target)
 * - Multi-selection from PlanEditor (multiple targets — beds/plantings)
 * - Plot/field-level actions
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { css } from '@emotion/react';
import {
  Modal,
  Radio,
  Button,
  Space,
  Typography,
  DatePicker,
  Select,
  Input,
  Steps,
  Tag,
  Divider,
  Alert,
} from 'antd';
import {
  ThunderboltOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ActionGrid from './ActionGrid';
import PropertyForm, { type PropertyFormValues } from './PropertyForm';
import type { ActionWizardProps, ActionMode, WizardStep, ActionTarget } from './ActionWizard.types';
import type { EventTemplate, EventTemplateDetail } from '../../types/eventTemplates';
import {
  useGetEventTemplatesQuery,
  useGetEventTemplateByIdQuery,
} from '../../api/eventTemplatesApi';
import {
  useCreatePlantingEventMutation,
  useCreateEventFromTemplateMutation,
} from '../../api/plantingEventsApi';
import { useGetPlantingHarvestInfoQuery } from '../../api/plantingsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateSimEventMutation } from '../../api/simulationApi';
import { useSave, useWorld } from '../../contexts/WorldContext';
import { useListBedsByPlotQuery } from '../../api/fieldsApi';
import { useGetSaveUsersQuery } from '../../api/saveUsersApi';
import type { PlantingEventPayload } from '../../types/plantingEvents';
import type { SimEventAction } from '../../types/simulation';
import { toastError, toastSuccess } from '../../utils/toast';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { datePickerFormat, formatNumber } from '../../i18n';

const { Text } = Typography;
const { TextArea } = Input;

const SCENARIO_WATERING_DEPTH_MM = 30;

const SCENARIO_WATERING_TEMPLATE_ID = -1001;

/** Built at render time so the name follows the current language. */
const scenarioWateringTemplate = (t: TFunction): EventTemplate => ({
  id: SCENARIO_WATERING_TEMPLATE_ID,
  code: 'watering',
  name: t('events:wizard.scenarioWatering.name'),
  description: t('events:wizard.scenarioWatering.description'),
  scopeType: 'system',
  saveId: null,
  category: 'care',
  targetType: 'bed',
  assignable: false,
  autoComplete: true,
  icon: '💧',
  color: '#52c41a',
  sortOrder: 0,
  isActive: true,
  properties: [],
});

const SAVE_SCOPED_EVENT_ACTIONS: ReadonlySet<SimEventAction> = new Set<SimEventAction>([
  'watering',
  'fertilizing',
  'pest_treatment',
  'pruning',
  'harvest',
  'composting',
  'liming',
  'acidifying',
  'aeration',
  'mulching',
  'gypsum',
]);

const BED_SCOPED_EVENT_ACTIONS: ReadonlySet<SimEventAction> = new Set<SimEventAction>([
  'watering',
  'fertilizing',
  'composting',
  'liming',
  'acidifying',
  'aeration',
  'mulching',
  'gypsum',
]);

const isSaveScopedEventAction = (code: string): code is SimEventAction => (
  SAVE_SCOPED_EVENT_ACTIONS.has(code as SimEventAction)
);

const resolveSaveScopedEventTargets = (
  action: SimEventAction,
  items: ActionTarget[]
): Array<{ plantingId?: string; bedId?: string }> => {
  const resolved: Array<{ plantingId?: string; bedId?: string }> = [];
  const seen = new Set<string>();

  items.forEach((target) => {
    if (BED_SCOPED_EVENT_ACTIONS.has(action)) {
      if (!target.bedId) return;
      const key = `bed:${target.bedId}`;
      if (seen.has(key)) return;
      seen.add(key);
      resolved.push({ bedId: target.bedId });
      return;
    }

    if (action === 'pest_treatment') {
      if (target.plantingId) {
        const key = `planting:${target.plantingId}`;
        if (seen.has(key)) return;
        seen.add(key);
        resolved.push({ plantingId: target.plantingId });
        return;
      }
      if (target.bedId) {
        const key = `bed:${target.bedId}`;
        if (seen.has(key)) return;
        seen.add(key);
        resolved.push({ bedId: target.bedId });
      }
      return;
    }

    if (!target.plantingId) return;
    const key = `planting:${target.plantingId}`;
    if (seen.has(key)) return;
    seen.add(key);
    resolved.push({ plantingId: target.plantingId });
  });

  return resolved;
};

const styles = {
  wizard: css({
    '.ant-modal-body': {
      padding: '16px 24px',
      maxHeight: 'calc(100vh - 200px)',
      overflowY: 'auto',
    },
  }),
  modeToggle: css({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 16,
  }),
  targetsBadge: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#f6ffed',
    borderRadius: 6,
    border: '1px solid #b7eb8f',
    marginBottom: 12,
  }),
  targetsMultiple: css({
    background: '#e6f7ff',
    border: '1px solid #91d5ff',
  }),
  assignForm: css({
    maxWidth: 480,
  }),
  stepContent: css({
    minHeight: 200,
  }),
  customActions: css({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    marginBottom: 12,
  }),
  customActionBtn: css({
    height: 'auto',
    minHeight: 72,
    textAlign: 'left',
  }),
};

type CustomPlantingAction = 'transplant' | 'to_storage';

const ActionWizard: React.FC<ActionWizardProps> = ({
  targets,
  farmId,
  cropId,
  plantingType,
  plantingQuantity,
  open,
  onClose,
  onSuccess,
  onStartTransplantSelection,
  contextLabel,
  simSessionId,
  simSaveId,
  simDisplayDate,
}) => {
  const { t } = useTranslation('events');
  const save = useSave();
  const world = useWorld();
  const actionDate = eventOpenDay(simDisplayDate || save.cursor?.simDate || save.cursor?.sim_date);
  const isScenarioSave = save.type === 'scenario';
  const effectiveSaveId = simSaveId || save.id;
  const isSimMode = !!simSessionId;
  const useScenarioFallbackActions = isScenarioSave;
  const useSaveScopedScenarioEvents = isSimMode || useScenarioFallbackActions;
  // ─── State ───────────────────────────────────────────
  const [step, setStep] = useState<WizardStep>('select-action');
  const [mode, setMode] = useState<ActionMode>('immediate');
  const [selectedTemplate, setSelectedTemplate] = useState<EventTemplate | null>(null);
  const [propertyValues, setPropertyValues] = useState<PropertyFormValues>({});
  const [notes, setNotes] = useState('');
  const [quantityAffected, setQuantityAffected] = useState<number | null>(null);
  const [quantityMode, setQuantityMode] = useState<'units' | 'percent'>('units');
  const [quantityPercent, setQuantityPercent] = useState<number | null>(null);
  // Simulation event date (used in sim immediate mode instead of dayjs())
  const [simEventDate, setSimEventDate] = useState<dayjs.Dayjs | null>(
    isSimMode && actionDate ? dayjs(actionDate) : null,
  );
  // Assign mode fields
  const [plannedDate, setPlannedDate] = useState<dayjs.Dayjs | null>(null);
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [assignNotes, setAssignNotes] = useState('');
  const [customAction, setCustomAction] = useState<CustomPlantingAction | null>(null);
  const [destinationBedId, setDestinationBedId] = useState<string | null>(null);

  // ─── API Queries ─────────────────────────────────────
  // Templates and membership are save-scoped. farmId remains only as target metadata.
  const templateSaveId = effectiveSaveId || save?.id || '';
  const { data: templatesResp, isLoading: loadingTemplates } = useGetEventTemplatesQuery(
    { saveId: templateSaveId, isActive: true },
    { skip: !templateSaveId || !open || isScenarioSave }
  );

  const { data: templateDetail, isLoading: loadingDetail } = useGetEventTemplateByIdQuery(
    { id: selectedTemplate?.id ?? 0, saveId: templateSaveId },
    { skip: !selectedTemplate || !templateSaveId || selectedTemplate.id < 0 }
  );

  const { data: farmUsers = [] } = useGetSaveUsersQuery(save.id, {
    skip: !save.id || mode !== 'assign',
  });

  // ─── API Mutations ───────────────────────────────────
  const [createPlantingEvent, { isLoading: creatingEvent }] = useCreatePlantingEventMutation();
  const [createFromTemplate, { isLoading: creatingFromTemplate }] = useCreateEventFromTemplateMutation();
  const [createSimEvent, { isLoading: creatingSimEvent }] = useCreateSimEventMutation();

  const submitting = creatingEvent || creatingFromTemplate || creatingSimEvent;

  // ─── Derived ─────────────────────────────────────────
  const scenarioAllowedActions = useMemo(() => {
    if (!isScenarioSave || targets.length === 0) return [];

    const actionsByTarget = targets.map((target) => {
      const targetKey = target.plantingId
        ? `planting:${target.plantingId}`
        : target.bedId
          ? `bed:${target.bedId}`
          : null;
      return targetKey ? (world.allowedActions.byTarget[targetKey] || []) : [];
    });

    return actionsByTarget[0].filter((action) => (
      actionsByTarget.every((actions) => actions.includes(action))
    ));
  }, [isScenarioSave, targets, world.allowedActions.byTarget]);

  const templates = useMemo(() => {
    if (!isScenarioSave) {
      return templatesResp?.templates ?? [];
    }
    return scenarioAllowedActions.includes('watering')
      ? [scenarioWateringTemplate(t)]
      : [];
  }, [isScenarioSave, scenarioAllowedActions, templatesResp?.templates, t]);
  const selectedTemplateDetail: EventTemplateDetail | null = useMemo(() => {
    if (!selectedTemplate) return null;
    if (selectedTemplate.id < 0) {
      return {
        ...selectedTemplate,
        properties: selectedTemplate.properties || [],
      };
    }
    return templateDetail ?? null;
  }, [selectedTemplate, templateDetail]);
  const isSinglePlanting = targets.length === 1 && !!targets[0]?.plantingId;
  const isMultiTarget = targets.length > 1;
  const isBatchPlanting = plantingType === 'batch' && (plantingQuantity ?? 0) > 1;
  const isPlantingTargetsOnly = targets.length > 0 && targets.every((t) => !!t.plantingId);

  // Harvest-info lookup: drives "Собрано (шт.)" input for whole-plant crops
  // (radish/carrot/cabbage/...) and the descriptive banner. Only fetch for a
  // single planting + harvest_collection template to keep traffic minimal.
  const isHarvestTemplate = selectedTemplate?.code === 'harvest_collection'
    || selectedTemplate?.code === 'harvest';
  const harvestInfoTarget = isSinglePlanting && isHarvestTemplate ? targets[0].plantingId! : null;
  const { data: harvestInfo, isFetching: loadingHarvestInfo } = useGetPlantingHarvestInfoQuery(
    harvestInfoTarget ? { plantingId: harvestInfoTarget } : skipToken,
  );
  const isWholePlantHarvest = harvestInfo?.harvestMode === 'whole_plant';
  const harvestRemaining = harvestInfo?.currentQuantity ?? null;
  const isWholePlantBatchHarvest = isHarvestTemplate
    && isWholePlantHarvest
    && harvestInfo?.plantingType === 'batch'
    && (harvestRemaining ?? 0) > 1;
  const showQuantityInput = isWholePlantBatchHarvest
    || (isBatchPlanting && !isHarvestTemplate);
  const harvestQuantityLimit = isHarvestTemplate && isWholePlantHarvest && harvestRemaining != null
    ? harvestRemaining
    : plantingQuantity;
  const estimatedHarvestKg = isHarvestTemplate
    && isWholePlantHarvest
    && quantityAffected != null
    && harvestInfo?.expectedYieldPerPlantKg != null
    ? Number((quantityAffected * harvestInfo.expectedYieldPerPlantKg).toFixed(3))
    : null;
  const harvestQuantityHelper = estimatedHarvestKg != null
    ? t('wizard.harvestHelper', {
      count: quantityAffected ?? 0,
      total: harvestQuantityLimit ?? plantingQuantity ?? 0,
      kg: formatNumber(estimatedHarvestKg, { maximumFractionDigits: 3 }),
    })
    : null;

  const sourceBedIds = useMemo(
    () => Array.from(new Set(targets.map((t) => t.bedId).filter(Boolean))) as string[],
    [targets]
  );
  const sourceBedId = sourceBedIds.length === 1 ? sourceBedIds[0] : null;
  const sourcePlotIds = useMemo(
    () => Array.from(new Set(targets.map((t) => t.plotId).filter(Boolean))) as string[],
    [targets]
  );
  const sourcePlotId = sourcePlotIds.length === 1 ? sourcePlotIds[0] : null;
  const canStartPlanTransplant = Boolean(
    mode === 'immediate' &&
    customAction === 'transplant' &&
    isPlantingTargetsOnly &&
    sourceBedId &&
    sourcePlotId &&
    onStartTransplantSelection
  );

  const { data: bedsInPlot = [] } = useListBedsByPlotQuery(sourcePlotId || '', {
    skip: !open || !sourcePlotId || customAction !== 'transplant' || mode !== 'immediate',
  });

  const destinationBeds = useMemo(
    () => bedsInPlot.filter((bed) => String(bed.id) !== sourceBedId),
    [bedsInPlot, sourceBedId]
  );

  // CN-0015: Determine what target types are present
  const hasPlantings = targets.some((t) => !!t.plantingId);
  const hasBeds = targets.some((t) => !!t.bedId);

  // Filter templates by target type compatibility
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      // targetType=null or 'any' → always shown
      if (!t.targetType || t.targetType === 'any') return true;
      // targetType='planting' → only if at least one target has plantingId
      if (t.targetType === 'planting') return hasPlantings;
      // targetType='bed' → only for bed-level actions (no planting targets)
      if (t.targetType === 'bed') return hasBeds && !hasPlantings;
      // targetType='area' → bed/plot/field actions, NOT for individual planting targets
      if (t.targetType === 'area') return !hasPlantings;
      return true;
    });
  }, [templates, hasPlantings, hasBeds]);

  const targetsLabel = useMemo(() => {
    if (contextLabel) return contextLabel;
    if (isMultiTarget) return t('wizard.objects', { count: targets.length });
    if (isSinglePlanting) return t('targets.planting');
    return t('wizard.event');
  }, [contextLabel, isMultiTarget, isSinglePlanting, targets.length, t]);

  // Whole-plant harvest: prefill "сколько собрано" with remaining quantity so
  // a single click finishes a small batch; user can edit before submit.
  useEffect(() => {
    if (
      isHarvestTemplate
      && isWholePlantHarvest
      && harvestRemaining != null
      && quantityAffected == null
    ) {
      setQuantityAffected(harvestRemaining);
      setQuantityPercent(100);
    }
  }, [isHarvestTemplate, isWholePlantHarvest, harvestRemaining, quantityAffected]);

  // ─── Reset on open/close ─────────────────────────────
  useEffect(() => {
    if (open) {
      setStep('select-action');
      setSelectedTemplate(null);
      setPropertyValues({});
      setNotes('');
      setQuantityAffected(null);
      setQuantityMode('units');
      setQuantityPercent(null);
      setSimEventDate(isSimMode && actionDate ? dayjs(actionDate) : null);
      setPlannedDate(isSimMode && actionDate ? dayjs(actionDate) : null);
      setAssignedTo(null);
      setAssignNotes('');
      setCustomAction(null);
      setDestinationBedId(null);
    }
  }, [open, isSimMode, actionDate]);

  useEffect(() => {
    if (mode === 'assign' && customAction) {
      setCustomAction(null);
    }
  }, [mode, customAction]);

  useEffect(() => {
    if (useScenarioFallbackActions && mode !== 'immediate') {
      setMode('immediate');
    }
  }, [mode, useScenarioFallbackActions]);

  // ─── Handlers ────────────────────────────────────────
  const handleSelectTemplate = useCallback((template: EventTemplate) => {
    setSelectedTemplate(template);
    setCustomAction(null);
    setPropertyValues({});
    setNotes('');
    setQuantityAffected(null);
    setQuantityMode('units');
    setQuantityPercent(null);
  }, []);

  const handleSelectCustomAction = useCallback((action: CustomPlantingAction) => {
    setCustomAction(action);
    setSelectedTemplate(null);
    setPropertyValues({});
    setQuantityAffected(null);
    setQuantityMode('units');
    setQuantityPercent(null);
  }, []);

  const handleQuantityChange = useCallback((value: number | null) => {
    const limit = Number(harvestQuantityLimit || plantingQuantity || 0);
    const nextQuantity = value == null
      ? null
      : Math.max(1, Math.min(limit || Number.MAX_SAFE_INTEGER, Math.floor(Number(value))));
    setQuantityAffected(nextQuantity);
    if (limit > 0 && nextQuantity != null) {
      setQuantityPercent(Number(((nextQuantity / limit) * 100).toFixed(1)));
    }
  }, [harvestQuantityLimit, plantingQuantity]);

  const handleQuantityPercentChange = useCallback((value: number | null) => {
    const limit = Number(harvestQuantityLimit || plantingQuantity || 0);
    const nextPercent = value == null
      ? null
      : Math.max(1, Math.min(100, Number(value)));
    setQuantityPercent(nextPercent);
    if (limit > 0 && nextPercent != null) {
      const nextQuantity = Math.max(1, Math.min(limit, Math.round((limit * nextPercent) / 100)));
      setQuantityAffected(nextQuantity);
    } else {
      setQuantityAffected(null);
    }
  }, [harvestQuantityLimit, plantingQuantity]);

  const handleQuantityModeChange = useCallback((nextMode: 'units' | 'percent') => {
    setQuantityMode(nextMode);
    const limit = Number(harvestQuantityLimit || plantingQuantity || 0);
    if (nextMode === 'percent' && limit > 0) {
      const nextPercent = quantityAffected != null
        ? Number(((quantityAffected / limit) * 100).toFixed(1))
        : 100;
      setQuantityPercent(nextPercent);
      setQuantityAffected(Math.max(1, Math.min(limit, Math.round((limit * nextPercent) / 100))));
    }
  }, [harvestQuantityLimit, plantingQuantity, quantityAffected]);

  const handleNext = useCallback(() => {
    if (step === 'select-action' && canStartPlanTransplant) {
      onStartTransplantSelection?.({
        targets,
        sourceBedId: sourceBedId!,
        sourcePlotId: sourcePlotId!,
      });
      onClose();
      return;
    }

    if (step === 'select-action' && (selectedTemplate || customAction)) {
      setStep('fill-data');
    }
  }, [
    step,
    selectedTemplate,
    customAction,
    canStartPlanTransplant,
    onStartTransplantSelection,
    targets,
    sourceBedId,
    sourcePlotId,
    onClose,
  ]);

  const handleBack = useCallback(() => {
    setStep('select-action');
  }, []);

  const handlePropertyChange = useCallback((propertyId: number, value: any) => {
    setPropertyValues((prev) => ({
      ...prev,
      [String(propertyId)]: value,
    }));
  }, []);

  /** Submit — Immediate mode: create event directly */
  const handleSubmitImmediate = useCallback(async () => {
    // Effective date: in sim mode use the date picker, otherwise real today
    const effectiveDateStr = isSimMode && simEventDate
      ? simEventDate.format('YYYY-MM-DD')
      : (isSimMode && actionDate) || dayjs().format('YYYY-MM-DD');

    if (customAction && isPlantingTargetsOnly) {
      try {
        const transplantPayload =
          customAction === 'to_storage'
            ? {
                mode: 'storage',
                fromDate: effectiveDateStr,
              }
            : {
                bedId: destinationBedId,
                fromDate: effectiveDateStr,
              };

        if (customAction === 'transplant' && !destinationBedId) {
          toastError(t('wizard.selectDestinationBed'));
          return;
        }

        for (const target of targets) {
          if (!target.plantingId) continue;
          await createPlantingEvent({
            saveId: effectiveSaveId,
            plantingId: target.plantingId,
            simSessionIdForInvalidation: simSessionId || undefined,
            payload: {
              kind: 'transplant',
              notes: notes || undefined,
              eventDate: effectiveDateStr,
              properties: {
                transplanting: JSON.stringify({
                  ...transplantPayload,
                  fromDate: effectiveDateStr,
                }),
              },
            },
          }).unwrap();
        }

        toastSuccess(
          customAction === 'to_storage'
            ? t('wizard.movedToStorage', { count: targets.length })
            : t('wizard.transplanted', { count: targets.length })
        );
        await onSuccess?.();
        onClose();
        return;
      } catch (error) {
        toastError(error);
        return;
      }
    }

    if (!selectedTemplate || targets.length === 0) return;

    try {
      const properties: NonNullable<PlantingEventPayload['properties']> = {};
      for (const [key, value] of Object.entries(propertyValues)) {
        if (value !== null && value !== undefined && value !== '') {
          properties[key] = value;
        }
      }
      if (quantityAffected != null) {
        properties.quantity_affected = quantityAffected;
      }

      if (useSaveScopedScenarioEvents && isSaveScopedEventAction(selectedTemplate.code)) {
        if (!effectiveSaveId) {
          toastError(t('wizard.noSimSave'));
          return;
        }

        const saveScopedTargets = resolveSaveScopedEventTargets(selectedTemplate.code, targets);
        if (saveScopedTargets.length === 0) {
          toastError(t('wizard.noSupportedTarget'));
          return;
        }

        const wateringDepthProperty = (selectedTemplateDetail?.properties || []).find(
          (p) => p.code === 'watering_depth_mm'
        );
        const wateringDepthRaw = wateringDepthProperty
          ? propertyValues[String(wateringDepthProperty.propertyId)]
          : null;
        const wateringDepthMm = selectedTemplate.code === 'watering'
          ? (useScenarioFallbackActions && selectedTemplate.id === SCENARIO_WATERING_TEMPLATE_ID
            ? SCENARIO_WATERING_DEPTH_MM
            : (wateringDepthRaw !== null
              && wateringDepthRaw !== undefined
              && wateringDepthRaw !== ''
              ? Number(wateringDepthRaw)
              : undefined))
          : undefined;

        for (const target of saveScopedTargets) {
          await createSimEvent({
            saveId: effectiveSaveId,
            sessionId: simSessionId || undefined,
            action: selectedTemplate.code,
            date: effectiveDateStr,
            notes: notes || undefined,
            ...(Object.keys(properties).length > 0 ? { properties } : {}),
            ...(wateringDepthMm !== undefined ? { wateringDepthMm } : {}),
            ...target,
          }).unwrap();
        }

        toastSuccess(
          saveScopedTargets.length === 1
            ? t('wizard.eventAdded', { name: selectedTemplate.name })
            : t('wizard.eventsAdded', { count: saveScopedTargets.length, name: selectedTemplate.name })
        );
        await onSuccess?.();
        onClose();
        return;
      }

      // ── Real mode ──
      // User-picked date for retroactive/scheduled events. Without this the
      // backend defaults actualDate to NOW(), so the "Дата события" input
      // looks like it works but silently does nothing.
      const userPickedDate = isSimMode && simEventDate
        ? effectiveDateStr
        : undefined;

      // For single planting target, use direct event creation
      if (isSinglePlanting) {
        const plantingId = targets[0].plantingId!;
        const result = await createPlantingEvent({
          saveId: effectiveSaveId,
          plantingId,
          simSessionIdForInvalidation: simSessionId || undefined,
          payload: {
            kind: selectedTemplate.code,
            notes: notes || undefined,
            properties: Object.keys(properties).length > 0 ? properties : undefined,
            ...(userPickedDate ? { eventDate: userPickedDate } : {}),
          },
        }).unwrap();

        toastSuccess(
          t('wizard.eventRecorded', { count: result.event.measuredPropertiesUpdated })
        );
      } else {
        // Multiple targets — use from-template endpoint with auto-complete
        const requestPayload: any = {
          templateId: selectedTemplate.id,
          farmId,
          targets: targets.map((t) => ({
            plantingId: t.plantingId || undefined,
            bedId: t.bedId || undefined,
            plotId: t.plotId || undefined,
            fieldId: t.fieldId || undefined,
          })),
          notes: notes || undefined,
          properties: Object.keys(properties).length > 0 ? properties : undefined,
          // Immediate execution: keep status=completed, drive actualDate.
          // Using plannedDate would flip isImmediateExecution=false on the
          // server and the event would be created as 'in_progress' instead.
          ...(userPickedDate ? { actualDate: userPickedDate } : {}),
        };

        await createFromTemplate({
          saveId: effectiveSaveId,
          payload: requestPayload,
          simSessionIdForInvalidation: simSessionId || undefined,
        }).unwrap();

        toastSuccess(t('wizard.eventCreatedFor', { count: targets.length }));
      }

      await onSuccess?.();
      onClose();
    } catch (error) {
      toastError(error);
    }
  }, [
    selectedTemplate, targets, isSinglePlanting, propertyValues, useScenarioFallbackActions,
    quantityAffected, notes, farmId, createPlantingEvent,
    createFromTemplate, createSimEvent, effectiveSaveId, isSimMode, simSessionId, simEventDate, actionDate,
    selectedTemplateDetail,
    onSuccess, onClose, customAction,
    isPlantingTargetsOnly, destinationBedId, useSaveScopedScenarioEvents, t,
  ]);

  /** Submit — Assign mode: create task(s) from template */
  const handleSubmitAssign = useCallback(async () => {
    if (!selectedTemplate || targets.length === 0) return;

    try {
      // Simulation mode: same endpoint, session-scoped event with selected date
      if (useSaveScopedScenarioEvents && isSaveScopedEventAction(selectedTemplate.code)) {
        if (!effectiveSaveId) {
          toastError(t('wizard.noSimSave'));
          return;
        }

        const eventDate = plannedDate?.format('YYYY-MM-DD') || actionDate || dayjs().format('YYYY-MM-DD');
        const saveScopedTargets = resolveSaveScopedEventTargets(selectedTemplate.code, targets);
        if (saveScopedTargets.length === 0) {
          toastError(t('wizard.noSupportedTarget'));
          return;
        }

        for (const target of saveScopedTargets) {
          await createSimEvent({
            saveId: effectiveSaveId,
            sessionId: simSessionId || undefined,
            action: selectedTemplate.code,
            date: eventDate,
            notes: assignNotes || undefined,
            ...target,
          }).unwrap();
        }

        toastSuccess(t('wizard.simEventsPlanned', { count: saveScopedTargets.length, name: selectedTemplate.name }));
        await onSuccess?.();
        onClose();
        return;
      }

      const result = await createFromTemplate({
        saveId: simSaveId || '',
        payload: {
          templateId: selectedTemplate.id,
          targets: targets.map((t) => ({
            plantingId: t.plantingId || undefined,
            bedId: t.bedId || undefined,
            plotId: t.plotId || undefined,
            fieldId: t.fieldId || undefined,
          })),
          plannedDate: plannedDate?.format('YYYY-MM-DD') || undefined,
          assignedTo: assignedTo || undefined,
          notes: assignNotes || undefined,
        },
      }).unwrap();

      const count = result.events?.length ?? targets.length;
      const assignee = assignedTo
        ? farmUsers.find((u) => u.user.id === assignedTo)?.user.name
        : null;

      toastSuccess(
        assignee
          ? t('wizard.tasksCreatedAssigned', { count, assignee })
          : t('wizard.tasksCreated', { count })
      );

      onSuccess?.();
      onClose();
    } catch (error) {
      toastError(error);
    }
  }, [
    selectedTemplate, targets, plannedDate, assignedTo, actionDate,
    assignNotes, farmUsers, createFromTemplate, createSimEvent,
    effectiveSaveId, simSaveId, simSessionId, onSuccess, onClose, useSaveScopedScenarioEvents, t,
  ]);

  const handleSubmit = mode === 'immediate' ? handleSubmitImmediate : handleSubmitAssign;

  // ─── Validate step 2 ────────────────────────────────
  const canSubmit = useMemo(() => {
    if (mode === 'immediate') {
      if (customAction === 'to_storage') return true;
      if (customAction === 'transplant') {
        return !!destinationBedId && !!sourceBedId && destinationBedId !== sourceBedId;
      }
      if (!selectedTemplate) return false;
      if (isHarvestTemplate && harvestInfoTarget && loadingHarvestInfo) return false;
      if (showQuantityInput && quantityAffected == null) return false;
      // Check required properties
      const props = selectedTemplateDetail?.properties ?? [];
      for (const p of props) {
        // Service/marker properties are hidden from the form, so don't gate submit on them.
        if (p.isRequired && !p.isService) {
          const val = propertyValues[String(p.propertyId)];
          if (val === null || val === undefined || val === '') return false;
        }
      }
      return true;
    }
    // Assign mode — no hard requirements (date and assignee are optional)
    return !!selectedTemplate;
  }, [
    selectedTemplate, mode, selectedTemplateDetail, propertyValues,
    customAction, destinationBedId, sourceBedId, isHarvestTemplate,
    harvestInfoTarget, loadingHarvestInfo, showQuantityInput, quantityAffected,
  ]);

  // ─── Footer buttons ─────────────────────────────────
  const footer = useMemo(() => {
    if (step === 'select-action') {
      return (
        <Space>
          <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button
            type="primary"
            onClick={handleNext}
              disabled={!selectedTemplate && !customAction}
          >
            {t('wizard.next')}
          </Button>
        </Space>
      );
    }
    return (
      <Space>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          {t('common:actions.back')}
        </Button>
        <Button
          type="primary"
          icon={mode === 'immediate' ? <CheckOutlined /> : <CalendarOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
        >
          {mode === 'immediate' ? t('wizard.recordButton') : t('wizard.assignButton')}
        </Button>
      </Space>
    );
  }, [step, selectedTemplate, customAction, mode, submitting, canSubmit, handleNext, handleBack, handleSubmit, onClose, t]);

  // ─── Render ──────────────────────────────────────────
  const modalTitle = useMemo(() => {
    const simPrefix = isSimMode ? '🧪 ' : '';
    if (step === 'select-action') return `${simPrefix}${t('wizard.newAction')}`;
    if (customAction === 'transplant') return t('wizard.transplant');
    if (customAction === 'to_storage') return t('wizard.toStorage');
    if (selectedTemplate) {
      return `${simPrefix}${selectedTemplate.icon || '📋'} ${selectedTemplate.name}`;
    }
    return `${simPrefix}${t('wizard.fillData')}`;
  }, [step, selectedTemplate, customAction, isSimMode, t]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={modalTitle}
      footer={footer}
      width={560}
      css={styles.wizard}
      destroyOnClose
    >
      {/* Target context badge */}
      <div css={[styles.targetsBadge, isMultiTarget && styles.targetsMultiple]}>
        {isMultiTarget ? (
          <TeamOutlined style={{ fontSize: 16, color: '#1890ff' }} />
        ) : (
          <span>🌱</span>
        )}
        <Text>{targetsLabel}</Text>
        {isBatchPlanting && (
          <Tag color="blue">{t('wizard.batchQuantity', { count: plantingQuantity ?? 0 })}</Tag>
        )}
        {isMultiTarget && (
          <Tag color="processing">{t('wizard.objects', { count: targets.length })}</Tag>
        )}
        {isSimMode && (
          <Tag color="orange">{t('wizard.simMode')}</Tag>
        )}
      </div>

      {/* Steps indicator */}
      <Steps
        size="small"
        current={step === 'select-action' ? 0 : 1}
        items={[
          { title: t('wizard.steps.selectAction') },
          { title: mode === 'immediate' ? t('wizard.steps.recordData') : t('wizard.steps.assignment') },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Step content */}
      <div css={styles.stepContent}>
        {step === 'select-action' && (
          <>
            {/* Mode toggle */}
            {!isScenarioSave && (
              <div css={styles.modeToggle}>
                <Radio.Group
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  buttonStyle="solid"
                  size="middle"
                >
                  <Radio.Button value="immediate">
                    <ThunderboltOutlined /> {t('wizard.recordNow')}
                  </Radio.Button>
                  <Radio.Button value="assign">
                    <CalendarOutlined /> {t('wizard.assignTask')}
                  </Radio.Button>
                </Radio.Group>
              </div>
            )}

            {useScenarioFallbackActions && (
              <Alert
                type="info"
                showIcon
                message={t('wizard.scenarioAction')}
                description={t('wizard.scenarioActionHint')}
                style={{ marginBottom: 12 }}
              />
            )}

            {/* Action grid (filtered by target type) */}
            {mode === 'immediate' && isPlantingTargetsOnly && !isScenarioSave && (
              <>
                <Alert
                  type="info"
                  showIcon
                  message={t('wizard.locationActions')}
                  description={t('wizard.locationActionsHint')}
                  style={{ marginBottom: 12 }}
                />
                <div css={styles.customActions}>
                  <Button
                    css={styles.customActionBtn}
                    type={customAction === 'transplant' ? 'primary' : 'default'}
                    onClick={() => handleSelectCustomAction('transplant')}
                  >
                    {t('wizard.transplant')}
                  </Button>
                  <Button
                    css={styles.customActionBtn}
                    type={customAction === 'to_storage' ? 'primary' : 'default'}
                    onClick={() => handleSelectCustomAction('to_storage')}
                  >
                    {t('wizard.toStorage')}
                  </Button>
                </div>
                {customAction === 'transplant' && canStartPlanTransplant && (
                  <Alert
                    type="success"
                    showIcon
                    message={t('wizard.pickBedOnPlan')}
                    style={{ marginBottom: 12 }}
                  />
                )}
              </>
            )}

            {!loadingTemplates && filteredTemplates.length === 0 && templates.length > 0 && (
              <Alert
                type="info"
                showIcon
                message={t('wizard.noMatchingTemplates')}
                description={
                  !hasPlantings
                    ? t('wizard.bedsWithoutPlantings')
                    : t('wizard.noTemplatesForTargets')
                }
                style={{ marginBottom: 12 }}
              />
            )}
            <ActionGrid
              templates={filteredTemplates}
              selectedTemplateId={selectedTemplate?.id ?? null}
              onSelect={handleSelectTemplate}
              loading={loadingTemplates}
              isSimMode={isSimMode}
            />
          </>
        )}

        {step === 'fill-data' && mode === 'immediate' && (
          customAction ? (
            <div css={styles.assignForm}>
              {customAction === 'transplant' && (
                <>
                  {!sourceBedId || !sourcePlotId ? (
                    <Alert
                      type="error"
                      showIcon
                      message={t('wizard.cannotTransplant')}
                      description={t('wizard.cannotTransplantHint')}
                      style={{ marginBottom: 12 }}
                    />
                  ) : (
                    <>
                      <Text strong style={{ display: 'block', marginBottom: 6 }}>
                        {t('wizard.destinationBed')}
                      </Text>
                      <Select
                        style={{ width: '100%', marginBottom: 12 }}
                        placeholder={t('wizard.selectBed')}
                        value={destinationBedId}
                        onChange={setDestinationBedId}
                        options={destinationBeds.map((bed) => ({
                          value: String(bed.id),
                          label: bed.name || t('wizard.bedNumber', { id: String(bed.id).slice(0, 8) }),
                        }))}
                      />
                    </>
                  )}
                </>
              )}

              {customAction === 'to_storage' && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('wizard.movingToStorage')}
                  description={t('wizard.movingToStorageHint')}
                  style={{ marginBottom: 12 }}
                />
              )}

              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {t('wizard.comment')}
              </Text>
              <TextArea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('wizard.commentPlaceholder')}
                maxLength={1000}
                showCount
              />

              {isSimMode && (
                <div style={{ marginTop: 12 }}>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    {t('wizard.simEventDate')}
                  </Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    format={datePickerFormat()}
                    value={simEventDate}
                    onChange={setSimEventDate}
                    disabledDate={disableBeforeOpenDay(actionDate)}
                    placeholder={t('wizard.simDate')}
                  />
                </div>
              )}
            </div>
          ) : (
          <>
          {isSimMode && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {t('wizard.simEventDate')}
              </Text>
              <DatePicker
                style={{ width: '100%' }}
                format={datePickerFormat()}
                value={simEventDate}
                onChange={setSimEventDate}
                disabledDate={disableBeforeOpenDay(actionDate)}
                placeholder={t('wizard.simDate')}
              />
            </div>
          )}
          {isHarvestTemplate && harvestInfo && (
            <Alert
              type={isWholePlantHarvest ? 'warning' : 'info'}
              showIcon
              style={{ marginBottom: 12 }}
              message={isWholePlantHarvest
                ? t('wizard.wholePlantHarvest', { count: harvestRemaining ?? 0 })
                : t('wizard.fruitingHarvest')}
              description={isWholePlantHarvest
                ? t('wizard.wholePlantHarvestHint')
                : t('wizard.fruitingHarvestHint', { count: harvestRemaining ?? plantingQuantity ?? 0 })}
            />
          )}
          <PropertyForm
            properties={selectedTemplateDetail?.properties ?? []}
            values={propertyValues}
            onChange={handlePropertyChange}
            notes={notes}
            onNotesChange={setNotes}
            showQuantity={showQuantityInput}
            plantingQuantity={harvestQuantityLimit}
            quantityAffected={quantityAffected}
            onQuantityChange={handleQuantityChange}
            allowQuantityPercent={isHarvestTemplate && isWholePlantBatchHarvest}
            quantityMode={quantityMode}
            onQuantityModeChange={handleQuantityModeChange}
            quantityPercent={quantityPercent}
            onQuantityPercentChange={handleQuantityPercentChange}
            quantityLabel={isHarvestTemplate && isWholePlantHarvest ? t('wizard.plantsHarvested') : undefined}
            quantityHelper={harvestQuantityHelper}
            loading={loadingDetail || loadingHarvestInfo}
          />
          </>
          )
        )}

        {step === 'fill-data' && mode === 'assign' && (
          <div css={styles.assignForm}>
            {/* What will need to be filled on completion (skip service/marker props) */}
            {(() => {
              const fillable = (templateDetail?.properties ?? []).filter(
                (p) => !p.isService && p.code !== 'quantity_affected'
              );
              if (fillable.length === 0) return null;
              return (
                <Alert
                  type="info"
                  showIcon
                  message={t('wizard.toFillOnCompletion')}
                  description={
                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                      {fillable.map((p) => (
                        <li key={p.propertyId}>
                          {p.name}
                          {p.unit ? ` (${p.unit})` : ''}
                          {p.isRequired ? ' *' : ''}
                        </li>
                      ))}
                    </ul>
                  }
                  style={{ marginBottom: 16 }}
                />
              );
            })()}

            <Divider orientation="left" orientationMargin={0}>
              {t('wizard.taskParameters')}
            </Divider>

            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  {t('wizard.plannedDate')}
                </Text>
                <DatePicker
                  style={{ width: '100%' }}
                  format={datePickerFormat()}
                  value={plannedDate}
                  onChange={setPlannedDate}
                  disabledDate={disableBeforeOpenDay(actionDate)}
                  placeholder={t('wizard.whenToDo')}
                />
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  {t('wizard.assignee')}
                </Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('wizard.assigneePlaceholder')}
                  value={assignedTo}
                  onChange={setAssignedTo}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={farmUsers.map((u) => ({
                    value: u.user.id,
                    label: `${u.user.name}${u.user.username ? ` (@${u.user.username})` : ''}`,
                  }))}
                />
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  {t('wizard.instructions')}
                </Text>
                <TextArea
                  rows={3}
                  value={assignNotes}
                  onChange={(e) => setAssignNotes(e.target.value)}
                  placeholder={t('wizard.instructionsPlaceholder')}
                  maxLength={1000}
                  showCount
                />
              </div>

              {isMultiTarget && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('wizard.willCreateTasks', { count: targets.length })}
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ActionWizard;
